import { useState, useEffect } from 'react';
import { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useLanguageStore } from '../lib/languageStore';
import { translate } from '../lib/translations';
import { TradeAuctionModal } from '../components/trades/TradeAuctionModal';
import { QuickCounterOfferModal } from '../components/trades/QuickCounterOfferModal';
import { AuctionSummaryCard } from '../components/trades/AuctionSummaryCard';
import { useUserStore } from '../lib/userStore';
import { toast } from 'react-hot-toast';
import { getActiveAuctions, getAuctionDetails, supabase } from '../lib/supabase';
import { useLeagueMode } from '../lib/hooks/useLeagueMode';
import { ModeAccessMessage } from '../components/shared/ModeAccessMessage';

// Interfaces de détails/dernière offre non utilisées ici (détails gérés dans la modal)

// Données renvoyées par get_active_auctions() (SQL)
interface AuctionData {
  id: number;
  original_trade_id: number;
  league_id: number;
  initiator_user_id: number;
  target_user_id: number;
  status: string;
  expires_at: string;
  created_at: string;
  initiator_username: string;
  target_username: string;
  league_name: string;
  original_sender_assets: any;
  original_receiver_assets: any;
  winning_bid_id: number | null;
  accepted_by_user_id: number | null;
  // facultatif — présent après enrichissement/groupement
  auction_id?: number;
  grouped_count?: number;
  grouped_ids?: number[];
}

export function ActiveAuctions() {
  const { isStandardFlexible, isLoading: modeLoading } = useLeagueMode();
  const navigate = useNavigate();
  const { language } = useLanguageStore();
  const { user_id } = useUserStore();
  const userId = user_id ? parseInt(user_id.toString()) : null;
  const [auctions, setAuctions] = useState<AuctionData[]>([]);
  const initialLoadRef = useRef(true);
  const [selectedAuction, setSelectedAuction] = useState<number | null>(null);
  const [selectedCounterOffer, setSelectedCounterOffer] = useState<{ auctionId: number; info: any } | null>(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'my' | 'others'>('all');

  useEffect(() => {
    console.log('🎯 selectedAuction changed:', selectedAuction);
  }, [selectedAuction]);

  useEffect(() => {
    console.log('🔥 selectedCounterOffer changed:', selectedCounterOffer);
  }, [selectedCounterOffer]);

  // Debug du modal state
  useEffect(() => {
    console.log('🚀 Debug modal state - selectedCounterOffer:', selectedCounterOffer, 'Type:', typeof selectedCounterOffer);
  }, [selectedCounterOffer]);

  const handleCounterOffer = (auctionId: number) => {
    console.log('🔥 Clic contre-offre pour enchère:', auctionId);
    
    // Fermer d'abord toute modal ouverte
    setSelectedAuction(null);
    
    const auction = auctions.find(a => a.id === auctionId);
    console.log('🔍 Enchère trouvée:', auction);
    if (auction) {
      const numericId = Number(auction.auction_id ?? auction.id ?? auctionId);
      if (isNaN(numericId)) {
        console.error('❌ Auction id could not be coerced to number:', auction, auctionId);
        toast.error("ID d'enchère invalide — impossible d'ouvrir la contre-offre");
        return;
      }

      const counterOfferData = {
        auctionId: numericId,
        info: {
          initiator_username: auction.initiator_username,
          target_username: auction.target_username,
          league_name: auction.league_name,
          league_id: auction.league_id,
          initiator_user_id: auction.initiator_user_id,
          target_user_id: auction.target_user_id
        }
      };
      console.log('📝 Setting selectedCounterOffer to (coerced):', counterOfferData);
      
      // Utiliser setTimeout pour s'assurer que le state est bien mis à jour
      setTimeout(() => {
        setSelectedCounterOffer(counterOfferData);
      }, 10);
    } else {
      console.error('❌ Enchère non trouvée avec ID:', auctionId);
    }
  };

  useEffect(() => {
    loadActiveAuctions();

    // Refresh toutes les 30 secondes
    const interval = setInterval(loadActiveAuctions, 30000);
    return () => clearInterval(interval);
  }, [user_id]);

  const loadActiveAuctions = async () => {
    try {
      console.log('🔄 Chargement des enchères actives...');
      const { data, error } = await getActiveAuctions();

      console.log('📊 Réponse getActiveAuctions:', { data, error });

      if (error) {
        console.error('Erreur chargement enchères:', error);
        toast.error('Erreur lors du chargement des enchères');
        return;
      }

      console.log('Données enchères reçues:', data); // Debug

      // Enrichir les enchères avec bid_count et latest_bid_username en chargeant les détails
      const enriched: AuctionData[] = (data || []).map((a: any) => ({ ...a }));

  // Pour performance, charger les details uniquement pour les enchères qui nous concernent
      await Promise.all(enriched.map(async (a: any, idx: number) => {
        try {
          // Déterminer l'ID d'enchère (le RPC peut renvoyer auction_id ou id selon la source)
          const auctionKey = a.id ?? a.auction_id ?? a.auctionId ?? a.auction?.auction_id;
          if (!auctionKey) {
            console.warn('ID d\'enchère manquant pour enrichment:', a);
            return;
          }

          // Charger les détails (bids) via l'RPC existant
          const { data: details, error: detErr } = await getAuctionDetails(auctionKey);
          if (detErr) {
            console.warn('Erreur getAuctionDetails pour', auctionKey, detErr);
            return;
          }

          // La fonction getAuctionDetails retourne un objet { success, auction, bids }
          if (details && details.success) {
            const auctionDetails = details;
            const bids = auctionDetails.bids || [];
            enriched[idx] = {
              ...enriched[idx],
              bid_count: bids.length,
              latest_bid_username: bids.length > 0 ? bids[0].bidder_username : undefined,
              winning_bid_id: auctionDetails.auction?.winning_bid_id ?? enriched[idx].winning_bid_id,
              accepted_by_user_id: auctionDetails.auction?.accepted_by_user_id ?? enriched[idx].accepted_by_user_id
            } as any;

            // Si les usernames initiaux manquent, tenter de les récupérer depuis users.user_id
            try {
              if (!enriched[idx].initiator_username && enriched[idx].initiator_user_id) {
                const { data: u1, error: u1Err } = await supabase.from('users').select('username').eq('user_id', enriched[idx].initiator_user_id).single();
                if (!u1Err && u1) enriched[idx].initiator_username = u1.username;
              }
              if (!enriched[idx].target_username && enriched[idx].target_user_id) {
                const { data: u2, error: u2Err } = await supabase.from('users').select('username').eq('user_id', enriched[idx].target_user_id).single();
                if (!u2Err && u2) enriched[idx].target_username = u2.username;
              }
            } catch (e) {
              // silencieux si la récupération user échoue
              console.warn('Impossible de récupérer username user_id', e);
            }

            // Si l'utilisateur connecté est le destinataire (target) ou initiateur, et qu'il y a une nouvelle offre
            try {
              const currentUserId = userId;
              if (currentUserId && (a.target_user_id === currentUserId || a.initiator_user_id === currentUserId)) {
                // Comparer avec le state courant pour déterminer nouvelle offre
                const prev = auctions.find(x => x.id === a.id);
                const prevCount = prev ? (prev as any).bid_count || 0 : 0;
                const newCount = bids.length;
                // Ne pas notifier lors du premier chargement massif
                if (!initialLoadRef.current && newCount > prevCount) {
                  toast.success(`Nouvelle contre-offre reçue pour l'enchère ${a.league_name} (${a.initiator_username} → ${a.target_username})`);
                }
              }
            } catch (e) {
              // Silencieux
            }
          }
        } catch (e) {
          console.error('Erreur enrichissement enchère', a.id, e);
        }
      }));

  setAuctions(enriched || []);
  // Après le premier chargement, autoriser les notifications de nouvelles offres
  if (initialLoadRef.current) initialLoadRef.current = false;
    } catch (error) {
      console.error('Erreur chargement enchères:', error);
      toast.error('Erreur lors du chargement des enchères');
    } finally {
      setLoading(false);
    }
  };

  let filteredAuctions = auctions.filter(auction => {
    if (filter === 'my') {
      return userId != null && (auction.initiator_user_id === userId || auction.target_user_id === userId);
    }
    if (filter === 'others') {
      return userId != null && (auction.initiator_user_id !== userId && auction.target_user_id !== userId);
    }
    return true; // 'all'
  });

  // Dedupe affichage : si plusieurs entrées identiques existent (même original_trade_id ou même participants+assets),
  // ne garder qu'une seule apparition pour éviter les doublons UI.
  try {
    const seen = new Set<string>();
    const deduped: typeof filteredAuctions = [];
    for (const a of filteredAuctions) {
      const key = a.original_trade_id != null
        ? `orig:${a.original_trade_id}`
        : `sig:${Math.min(a.initiator_user_id, a.target_user_id)}-${Math.max(a.initiator_user_id, a.target_user_id)}-${JSON.stringify(a.original_sender_assets)}|${JSON.stringify(a.original_receiver_assets)}`;
      if (!seen.has(key)) {
        seen.add(key);
        deduped.push(a);
      }
    }
    filteredAuctions = deduped;
  } catch (e) {
    console.warn('Erreur dedupe auctions:', e);
  }

  // Regrouper les enchères par original_trade_id pour éviter les doublons visuels
  const groupByOriginalTrade = (items: AuctionData[]) => {
    const map = new Map<number, AuctionData & { grouped_count?: number }>();
    for (const item of items) {
      const key = item.original_trade_id;
      if (!map.has(key)) {
        map.set(key, { ...item, grouped_count: 1, grouped_ids: [item.id] } as any);
      } else {
        const existing = map.get(key)!;
        const newCount = (existing.grouped_count || 1) + 1;
        // Conserver l'enchère la plus urgente (celle qui expire le plus tôt) comme représentant
        const existingExpires = new Date(existing.expires_at).getTime();
        const itemExpires = new Date(item.expires_at).getTime();
        if (itemExpires < existingExpires) {
          map.set(key, { ...item, grouped_count: newCount, grouped_ids: [...(existing as any).grouped_ids || [], item.id] } as any);
        } else {
          map.set(key, { ...existing, grouped_count: newCount, grouped_ids: [...(existing as any).grouped_ids || [], item.id] } as any);
        }
      }
    }
    return Array.from(map.values());
  };

  const groupedAuctions = groupByOriginalTrade(filteredAuctions);

  // Choix d'affichage :
  // - si filter === 'all' on affiche toutes les enchères non-groupées (chaque entrée)
  // - sinon (my/others) on affiche les enchères groupées pour éviter les doublons
  const displayAuctions = filter === 'all' ? filteredAuctions : groupedAuctions;

  // Urgent: moins d'1h restante (calculé sur la liste affichée)
  const urgentAuctions = displayAuctions.filter(a => {
    const diffMs = new Date(a.expires_at).getTime() - Date.now();
    return diffMs > 0 && diffMs < 60 * 60 * 1000;
  });
  const acceptedAuctions = displayAuctions.filter(auction => auction.winning_bid_id !== null);
  const pendingAuctions = displayAuctions.filter(auction => auction.winning_bid_id === null);

  // Comptes pour les boutons de filtre : si 'all' compter les items non-groupés, sinon compter les groupes
  const totalCount = filter === 'all' ? filteredAuctions.length : groupedAuctions.length;
  const myCount = filter === 'all'
    ? filteredAuctions.filter(a => a.initiator_user_id === userId || a.target_user_id === userId).length
    : groupedAuctions.filter(a => a.initiator_user_id === userId || a.target_user_id === userId).length;
  const othersCount = filter === 'all'
    ? filteredAuctions.filter(a => a.initiator_user_id !== userId && a.target_user_id !== userId).length
    : groupedAuctions.filter(a => a.initiator_user_id !== userId && a.target_user_id !== userId).length;

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-6">
        <div className="flex justify-center py-12">
          <span className="loading loading-spinner loading-lg"></span>
        </div>
      </div>
    );
  }

  // Mode guard
  if (!modeLoading && isStandardFlexible) {
    return (
      <ModeAccessMessage
        variant="dynasty-only"
        title="Enchères d'échanges — Mode Dynasty / Keeper uniquement"
        message="Les enchères d'échanges sont une fonctionnalité du mode Dynasty/Keeper. En mode Standard Flexible, les échanges ne sont pas disponibles."
      />
    );
  }

  return (
    <div className="container mx-auto px-4 py-6">
      {/* Header avec statistiques */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
        <div>
            <div className="flex items-center gap-3">
              <button
                className="btn btn-link text-primary mb-4 p-0 d-flex align-items-center"
                onClick={() => navigate('/my-office')}
              >
                <ArrowLeft size={20} className="me-2" />
                {translate('back', language)}
              </button>
              <h1 className="text-3xl font-bold flex items-center gap-3">
                🔥 Enchères Actives
                <div className="badge badge-primary badge-lg">{totalCount}</div>
              </h1>
            </div>
          <p className="text-sm opacity-70 mt-1">
            Système de contre-offres - Les échanges avec enchères ouvertes
          </p>
        </div>

        {/* Statistiques rapides */}
        <div className="grid grid-cols-3 gap-2 text-center">
          <div className="stat bg-base-200 rounded-lg p-3">
            <div className="stat-value text-lg text-warning">{urgentAuctions.length}</div>
            <div className="stat-desc text-xs">{'Urgent (< 1h)'}</div>
          </div>
          <div className="stat bg-base-200 rounded-lg p-3">
            <div className="stat-value text-lg text-success">{acceptedAuctions.length}</div>
            <div className="stat-desc text-xs">Acceptées</div>
          </div>
          <div className="stat bg-base-200 rounded-lg p-3">
            <div className="stat-value text-lg text-info">{pendingAuctions.length}</div>
            <div className="stat-desc text-xs">En attente</div>
          </div>
        </div>
      </div>

      {/* Filtres */}
      <div className="flex gap-2 mb-6">
        <button 
          className={`btn btn-sm ${filter === 'all' ? 'btn-primary' : 'btn-outline'}`}
          onClick={() => setFilter('all')}
        >
          Toutes ({totalCount})
        </button>
        <button 
          className={`btn btn-sm ${filter === 'my' ? 'btn-primary' : 'btn-outline'}`}
          onClick={() => setFilter('my')}
        >
          Mes enchères ({myCount})
        </button>
        <button 
          className={`btn btn-sm ${filter === 'others' ? 'btn-primary' : 'btn-outline'}`}
          onClick={() => setFilter('others')}
        >
          Autres ({othersCount})
        </button>
      </div>

      {/* Enchères urgentes (moins d'1h) */}
      {urgentAuctions.length > 0 && (
        <div className="mb-8">
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2 text-warning">
            ⚡ Enchères Urgentes (moins d'1h restante)
          </h2>
          <div className="grid gap-4">
            {urgentAuctions.map((auction) => (
              <AuctionSummaryCard
                key={auction.id}
                auction={{
                  auction_id: auction.id,
                  league_name: auction.league_name,
                  initiator_username: auction.initiator_username,
                  target_username: auction.target_username,
                  original_sender_assets: auction.original_sender_assets,
                  original_receiver_assets: auction.original_receiver_assets,
                  expires_at: auction.expires_at,
                  bid_count: 0,
                  winning_bid_id: auction.winning_bid_id ?? null,
                }}
                onClick={() => { 
                  const aid = auction.auction_id ?? auction.id;
                  console.log('Clic modal pour enchère urgente:', aid); 
                  setSelectedAuction(aid as number); 
                }}
                onCounterOffer={handleCounterOffer}
              />
            ))}
          </div>
        </div>
      )}

      {/* Enchères avec offres acceptées */}
      {acceptedAuctions.length > 0 && (
        <div className="mb-8">
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2 text-success">
            ✅ Enchères avec Offres Acceptées
          </h2>
          <div className="grid gap-4">
            {acceptedAuctions.filter(a => !urgentAuctions.includes(a)).map((auction) => (
              <AuctionSummaryCard
                key={auction.id}
                auction={{
                  auction_id: auction.id,
                  league_name: auction.league_name,
                  initiator_username: auction.initiator_username,
                  target_username: auction.target_username,
                  original_sender_assets: auction.original_sender_assets,
                  original_receiver_assets: auction.original_receiver_assets,
                  expires_at: auction.expires_at,
                  bid_count: 0,
                  winning_bid_id: auction.winning_bid_id ?? null,
                }}
                onClick={() => setSelectedAuction(auction.auction_id ?? auction.id)}
                onCounterOffer={handleCounterOffer}
              />
            ))}
          </div>
        </div>
      )}

      {/* Enchères en attente */}
      {pendingAuctions.length > 0 && (
        <div className="mb-8">
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
            🎯 Enchères en Attente d'Acceptation
          </h2>
          <div className="grid gap-4">
            {pendingAuctions.filter(a => !urgentAuctions.includes(a)).map((auction) => (
              <AuctionSummaryCard
                key={auction.id}
                auction={{
                  auction_id: auction.id,
                  league_name: auction.league_name,
                  initiator_username: auction.initiator_username,
                  target_username: auction.target_username,
                  original_sender_assets: auction.original_sender_assets,
                  original_receiver_assets: auction.original_receiver_assets,
                  expires_at: auction.expires_at,
                  bid_count: 0,
                  winning_bid_id: auction.winning_bid_id ?? null,
                }}
                onClick={() => setSelectedAuction(auction.auction_id ?? auction.id)}
                onCounterOffer={handleCounterOffer}
              />
            ))}
          </div>
        </div>
      )}

      {/* État vide */}
      {filteredAuctions.length === 0 && (
        <div className="text-center py-16">
          <div className="text-6xl mb-4">🏒</div>
          <h3 className="text-2xl font-semibold mb-2">Aucune enchère active</h3>
          <p className="opacity-60 mb-6">
            {filter === 'my' 
              ? 'Vous n\'avez aucune enchère en cours. Créez un échange avec l\'option "Permettre contre-offres" pour démarrer une enchère.'
              : filter === 'others'
              ? 'Aucune enchère créée par d\'autres DG pour le moment.'
              : 'Les enchères apparaîtront ici lorsque des DG activeront les contre-offres sur leurs échanges.'
            }
          </p>
          
          {filter !== 'others' && (
            <div className="space-y-2">
              <p className="text-sm opacity-80">
                <strong>💡 Comment créer une enchère :</strong>
              </p>
              <ol className="text-sm opacity-70 list-decimal list-inside space-y-1 max-w-md mx-auto">
                <li>Allez dans "Transactions"</li>
                <li>Créez un nouvel échange</li>
                <li>Cochez "Permettre les contre-offres"</li>
                <li>Soumettez votre échange</li>
              </ol>
            </div>
          )}
        </div>
      )}

      {/* Bouton de rafraîchissement */}
      <div className="fixed bottom-6 right-6">
        <button 
          onClick={loadActiveAuctions}
          className="btn btn-circle btn-primary shadow-lg"
          title="Actualiser"
        >
          🔄
        </button>
      </div>

      {/* Modal détaillée */}
      {selectedAuction && (
        <>
          {console.log('🎬 Rendu TradeAuctionModal pour auctionId:', selectedAuction)}
          <TradeAuctionModal 
            auctionId={selectedAuction}
            onClose={() => setSelectedAuction(null)}
          />
        </>
      )}

      {/* Modal contre-offre rapide */}
      {selectedCounterOffer && (
        <>
          <QuickCounterOfferModal
            auctionId={selectedCounterOffer.auctionId}
            auctionInfo={selectedCounterOffer.info}
            onClose={() => {
              console.log('❌ Fermeture modal contre-offre');
              setSelectedCounterOffer(null);
            }}
            onSuccess={() => {
              console.log('✅ Succès contre-offre');
              loadActiveAuctions(); // Recharger les enchères
            }}
          />
        </>
      )}
    </div>
  );
}